/**
 * The label registry, loaded once per screen.
 *
 * Both workspaces need it - the mail rail and the call rail each render their
 * own section, and both overflow menus offer the same list - so it is one hook
 * rather than a copy in each section. One request for both desks: the payload is
 * two short arrays and the endpoint answers them together.
 *
 * No manual refresh, on purpose. The editor lives on `/settings/labels` and
 * `CrmLayout` keys the routed area on the first path segment, so coming back to
 * a desk remounts it and re-reads the registry. A `reload()` nobody calls is
 * dead weight that reads like a promise the hook does not keep.
 *
 * Failure is silent, the same way `useCampaignNames` is. If the request fails or
 * the operator holds none of the three desk rights, the desk still has to open;
 * it simply shows no labels section, which is also what an operator with an
 * empty registry sees.
 */
import { useEffect, useState } from "react";
import { EMPTY_LABEL_SETS, getCrmLabels, type CrmLabelSets } from "../lib/crmLabels";

export function useCrmLabels(): { labels: CrmLabelSets } {
  const [labels, setLabels] = useState<CrmLabelSets>(EMPTY_LABEL_SETS);

  useEffect(() => {
    let alive = true;
    getCrmLabels()
      .then((sets) => {
        if (alive) setLabels(sets);
      })
      .catch(() => {
        /* the desk works without labels */
      });
    return () => {
      alive = false;
    };
  }, []);

  return { labels };
}
